import dayjs from 'dayjs';
import { isEndTimePastMidnight } from 'helpers/menuManagementHours';
import { Flex, Group, Stack, Text, Switch } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { breakpoints } from 'styles/theme.variables';

import CalendarSelect from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/CalendarSelect';
import EditScheduleMenu from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/EditScheduleMenu';
import { ErrorCountBadge } from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/ErrorCountBadge';
import TimeSelect from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/TimeSelect';
import { TimeOption } from 'components/MenuManagement/MenuEdit/Hours/types';

import {
  isDayClosed,
  getScheduleTimeValue,
  resolvePrimaryId,
} from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/helpers';

type Schedule = {
  uuid: string;
  tempId?: string;
  startTime: string;
  endTime: string;
  dayIndex?: string;
  appliedDate: string;
  isDeleted?: boolean;
  isPeriod?: boolean;
};

type HolidayHoursInputViewProps = {
  schedules: Schedule[];
  timeOptions: TimeOption[];
  handleToggle: (id: string) => void;
  handleChange: (val: string, id: string, type: string) => void;
  handle24Hours: (id: string) => void;
  handleDeleteHours: (id: string) => void;
  handleAddPeriod: (id: string) => void;
};

export default function HolidayHoursInputView({
  schedules,
  timeOptions,
  handleToggle,
  handleChange,
  handle24Hours,
  handleDeleteHours,
  handleAddPeriod,
}: HolidayHoursInputViewProps) {
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`, undefined, {
    getInitialValueInEffect: false,
  });
  const visibleSchedules = schedules.filter(schedule => !schedule.isDeleted);

  return (
    <Stack gap={isMobile ? 'xl' : 'md'}>
      {visibleSchedules.map(schedule => {
        const scheduleForHelpers = { ...schedule, dayIndex: schedule.dayIndex ?? '' };
        const scheduleId = resolvePrimaryId(scheduleForHelpers);

        if (!scheduleId) return null;

        return (
          <Stack gap="xs" key={scheduleId}>
            <Flex
              direction={isMobile ? 'column' : 'row'}
              gap={isMobile ? 'sm' : 'xs'}
              wrap="nowrap"
              maw={isMobile ? undefined : 700}
              align={isMobile ? undefined : 'center'}
            >
              <Switch
                checked={!isDayClosed(scheduleForHelpers)}
                onChange={() => handleToggle(scheduleId)}
                label={
                  <CalendarSelect
                    value={dayjs(schedule.appliedDate).format('MM/DD/YYYY')}
                    onSelectNewDay={v =>
                      handleChange(dayjs(v).format('YYYY-MM-DD'), scheduleId, 'appliedDate')
                    }
                  />
                }
                size="xs"
              />
              <Group gap="xs2" wrap="nowrap">
                <TimeSelect
                  timeOptions={timeOptions}
                  value={getScheduleTimeValue(scheduleForHelpers, timeOptions)}
                  onSelectNewTime={v => handleChange(v, scheduleId, 'startTime')}
                  disabled={isDayClosed(scheduleForHelpers)}
                  w={isMobile ? undefined : 140}
                  flex={isMobile ? 1 : undefined}
                />
                <Text component="span" mx={isMobile ? 'xs3' : 0}>
                  —
                </Text>
                <TimeSelect
                  timeOptions={timeOptions}
                  value={getScheduleTimeValue(scheduleForHelpers, timeOptions, { isEndTime: true })}
                  onSelectNewTime={v => handleChange(v, scheduleId, 'endTime')}
                  disabled={isDayClosed(scheduleForHelpers)}
                  w={isMobile ? undefined : 140}
                  flex={isMobile ? 1 : undefined}
                />
                <EditScheduleMenu
                  schedule={schedule}
                  handle24Hours={() => handle24Hours(scheduleId)}
                  handleDeleteHours={() => handleDeleteHours(scheduleId)}
                  handleAddPeriod={() => handleAddPeriod(scheduleId)}
                  isSpecial
                />
                <ErrorCountBadge
                  validationErrors={[]} // validation not implemented for holiday hours
                  scheduleId={scheduleId}
                  hasRollover={isEndTimePastMidnight({
                    start: schedule.startTime,
                    end: schedule.endTime,
                  })}
                />
              </Group>
            </Flex>
          </Stack>
        );
      })}
    </Stack>
  );
}
